import React, { useEffect, useRef, useState } from "react";
import "./Projects.scss";
import projects from "../data/projectsMono";
import useInView from "../hooks/useInView";
import { FaGithub, FaStar } from "react-icons/fa";
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiChevronUp,
  FiExternalLink,
  FiGlobe,
} from "react-icons/fi";

/* ---------- 이미지 캐러셀 ---------- */
function Carousel({ images, title, fit }) {
  const [index, setIndex] = useState(0);
  const total = images.length;

  const go = (delta) => setIndex((prev) => (prev + delta + total) % total);

  return (
    <div className={`carousel ${fit === "contain" ? "fit-contain" : ""}`}>
      <div
        className="carousel-track"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {images.map((src, i) => (
          <div className="carousel-slide" key={src}>
            <img
              src={src}
              alt={`${title} 스크린샷 ${i + 1}`}
              loading={i === 0 ? "eager" : "lazy"}
            />
          </div>
        ))}
      </div>

      <button
        type="button"
        className="carousel-btn prev"
        onClick={() => go(-1)}
        aria-label="이전 스크린샷"
      >
        <FiChevronLeft />
      </button>
      <button
        type="button"
        className="carousel-btn next"
        onClick={() => go(1)}
        aria-label="다음 스크린샷"
      >
        <FiChevronRight />
      </button>

      <span className="carousel-counter">
        {index + 1} / {total}
      </span>
    </div>
  );
}

/* ---------- 발표자료 임베드 (1440 논리 뷰포트를 축소) ---------- */
const EMBED_W = 1440;
const EMBED_H = 810;

function EmbedFrame({ url, title }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(null);

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;

    const update = () => setScale(node.clientWidth / EMBED_W);
    update();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }

    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="embed-shell">
      <div className="embed-bar">
        <span>{title}</span>
        <a href={url} target="_blank" rel="noopener noreferrer">
          새 창에서 열기 <FiExternalLink />
        </a>
      </div>
      <div className="embed-frame" ref={wrapRef}>
        {scale !== null && (
          <iframe
            src={url}
            title={title}
            allowFullScreen
            referrerPolicy="no-referrer"
            sandbox="allow-scripts allow-same-origin allow-popups"
            style={{
              width: `${EMBED_W}px`,
              height: `${EMBED_H}px`,
              transform: `scale(${scale})`,
            }}
          />
        )}
      </div>
    </div>
  );
}

/* ---------- 유튜브 시연 영상 ---------- */
function YoutubeEmbed({ id, title }) {
  return (
    <div className="embed-shell compact">
      <div className="embed-bar">
        <span>{title}</span>
        <a
          href={`https://youtu.be/${id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          YouTube에서 보기 <FiExternalLink />
        </a>
      </div>
      <div className="video-frame">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}`}
          title={title}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    </div>
  );
}

/* ---------- 프로젝트 1건 ---------- */
function ProjectEntry({ project }) {
  const [ref, inView] = useInView({ threshold: 0.08 });
  // 스크롤 길이를 줄이기 위해 주요 프로젝트만 기본으로 펼친다.
  // defaultOpen이 명시되면 그 값을 우선한다.
  const [open, setOpen] = useState(project.defaultOpen ?? project.featured);

  return (
    <article
      className={`project reveal ${inView ? "is-in" : ""}`}
      id={`project-${project.id}`}
      ref={ref}
    >
      <header className="project-head">
        <h3>{project.title}</h3>
        {project.badges.map((badge) => (
          <span className={`badge badge-${badge.tone}`} key={badge.text}>
            {badge.text}
          </span>
        ))}
      </header>

      <dl className="project-periods">
        {project.periods.map((period) => (
          <div key={period.label}>
            <dt>{period.label}</dt>
            <dd>{period.value}</dd>
          </div>
        ))}
      </dl>

      <div className="project-desc">
        {project.paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>

      {project.metrics && (
        <dl className="project-metrics">
          {project.metrics.map((metric) => (
            <div key={metric.label}>
              <dt>{metric.label}</dt>
              <dd>{metric.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="project-tags">
        {project.tags.map((tag) => (
          <span className="chip" key={tag}>
            {tag}
          </span>
        ))}
      </div>

      {open && (
        <div className="project-detail">
          <ul className="project-roles">
            {project.roles.map((role) => (
              <li key={role}>{role}</li>
            ))}
          </ul>

          {project.images && (
            <Carousel
              images={project.images}
              title={project.title}
              fit={project.imageFit}
            />
          )}

          {project.youtube && (
            <YoutubeEmbed id={project.youtube.id} title={project.youtube.title} />
          )}

          {project.embed && (
            <EmbedFrame url={project.embed.url} title={project.embed.title} />
          )}

          {(project.github || project.website) && (
            <div className="project-links">
              {project.website && (
                <a
                  href={project.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-btn primary"
                >
                  <FiGlobe />
                  Website
                </a>
              )}
              {project.github && (
                <a
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-btn"
                >
                  <FaGithub />
                  Github
                </a>
              )}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        className="project-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? (
          <>
            접기 <FiChevronUp />
          </>
        ) : (
          <>
            상세 내용 · 실행 화면 보기 <FiChevronDown />
          </>
        )}
      </button>
    </article>
  );
}

/* ---------- 섹션 ---------- */
function Projects() {
  const [ref, inView] = useInView();
  const [featuredOnly, setFeaturedOnly] = useState(false);

  const list = featuredOnly ? projects.filter((p) => p.featured) : projects;

  return (
    <section className="section projects" id="projects" ref={ref}>
      <div className="container">
        <h2 className={`section-title reveal ${inView ? "is-in" : ""}`}>
          Projects
        </h2>

        <div className={`projects-toolbar reveal ${inView ? "is-in" : ""}`}>
          <span className="projects-count">총 {projects.length}개 프로젝트</span>

          <button
            type="button"
            className={`featured-toggle ${featuredOnly ? "on" : ""}`}
            onClick={() => setFeaturedOnly((v) => !v)}
            aria-pressed={featuredOnly}
          >
            <FaStar />
            주요 프로젝트만
          </button>
        </div>

        <div className="projects-list">
          {list.map((project, index) => (
            <ProjectEntry project={project} key={project.id} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default Projects;
